import { db } from "astrid-firebase";
import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import sessionOfferTypes from "astrid-firestore/src/constants/sessionOfferTypes";
import { useCollectionData } from "astrid-firestore/src/hooks";

import { useProfile } from "../../../../authentication/state/profile";
import useHasRight from "../../../../authorization/hooks/useHasRight";

const getSessionsQuery = ({ organization, hasRight, user }) => {
	if (organization.type === organizationTypes.NARRATOR) {
		return db.collection("sessions").where("reader", "array-contains", organization.id);
	}

	if (!hasRight("calendar.viewTimeline")) {
		return db.collection("sessions").where("teamUserIds", "array-contains", user.id);
	}

	return db.collection("sessions").where("producer", "==", organization.id);
};

export default function useCalendarSessions({ startDate, organization, endDate, modifiedSessions, filters, viewType }) {
	const hasRight = useHasRight();
	const user = useProfile();

	const [sessions = [], loading, error] = useCollectionData(
		startDate &&
			endDate &&
			getSessionsQuery({ organization, hasRight, user })
				.where("start", ">=", startDate)
				.where("start", "<", endDate),
	);

	if (error) {
		console.error(error);
	}

	let filteredSessions = sessions.filter((session) => session.end.toDate() < endDate);

	if (filters.studios.length > 0 && viewType.includes("timeGrid")) {
		filteredSessions = filteredSessions.filter(
			(session) => modifiedSessions?.[session.id] || filters.studios?.includes(session.studio),
		);
	}

	const isFiltersActive =
		filters.languages.length > 0 ||
		filters.productions.length > 0 ||
		filters.readers.length > 0 ||
		filters.recorders.length > 0 ||
		filters.managers.length > 0;

	if (isFiltersActive) {
		filteredSessions = filteredSessions.filter((session) => {
			const isAnyRecorderInTeam = session.recorder === sessionOfferTypes.ANY_RECORDER_IN_TEAM;
			const isOpenPosition = session.recorder === sessionOfferTypes.OPEN_POSITION_RECORDER;

			return (
				modifiedSessions?.[session.id] ||
				(filters.languages.length > 0 && filters.languages.includes(session.productionData.language)) ||
				(filters.productions.length > 0 && filters.productions.includes(session.production)) ||
				(filters.readers.length > 0 && session.reader.find((reader) => filters.readers.includes(reader))) ||
				filters.recorders.includes(session.recorder) ||
				(filters.recorders.includes("noTechnicianBooked") &&
					(isOpenPosition ||
						(isAnyRecorderInTeam &&
							(!filters.hideUnavailable || session.teamUserIds?.includes(user.id))))) ||
				(filters.managers.length > 0 &&
					session.managerIds?.find((manager) => filters.managers.includes(manager)))
			);
		});
	}

	if (filters.statusFilters.booked || filters.statusFilters.cancelled || filters.statusFilters.completed) {
		filteredSessions = filteredSessions.filter(
			(session) =>
				modifiedSessions?.[session.id] ||
				(filters.statusFilters.booked && session.status === "booked") ||
				(filters.statusFilters.cancelled && session.status === "cancelled") ||
				(filters.statusFilters.completed && session.status === "done"),
		);
	}

	return [sessions, filteredSessions, loading, error];
}
